/**
 * @fileoverview `fallbackT` — the translator a shell module uses when no real
 *               one was supplied.
 * @package @puffin-core/page-shell
 *
 * A **leaf module with no React in its import chain**, deliberately, for the same
 * reason `strings/namespace.js` is one: its consumers include pure helpers
 * documented as "no React, no browser APIs". Living inside
 * `hooks/useShellTranslation.js` would drag `react-i18next` into the import chain
 * of every one of them. Its one import, the shared plural selector
 * (`@puffin-core/i18n-bootstrap/plural-keys`), is `Intl` only (Q-R8CLOSE-2).
 *
 * ## Why it exists at all (AAS-I18N-002 Lane F-3)
 *
 * RECIPE §3: a PLAIN MODULE cannot hold a hook, so it takes `t` as a parameter.
 * For a PUBLISHED pure helper that parameter must stay OPTIONAL — a caller doing
 * pure parsing with no UI must not be forced to supply a translator. The obvious
 * shape for that is a ternary per message:
 *
 * ```js
 * typeof t === 'function'
 *   ? t('presetImport.empty', { defaultValue: 'Import is empty' })
 *   : 'Import is empty';
 * ```
 *
 * which writes the English **twice per message**, in two places that can drift
 * apart silently — and the drift is invisible, because each arm is only exercised
 * by half the tests. Using this as a DEFAULT PARAMETER collapses both arms to one
 * code path with exactly one English string, while preserving the published
 * "absent `t` ⇒ English" contract byte-for-byte.
 *
 * 🔴 It also interpolates, which react-i18next's own no-instance `t` does NOT —
 * that omission shipped a literal `aria-label="Restore {{label}}"` on 2026-08-09
 * with lint 0, census 0 and parity green.
 */

import { selectPluralKey } from '@puffin-core/i18n-bootstrap/plural-keys';

/** i18next's default interpolation shape: `{{name}}`, tolerant of inner spaces. */
const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/**
 * 🔴 A genuinely locale-INVARIANT use, declared by name per
 * `web/no-hardcoded-intl-locale`'s doctrine (the rule grants no path allow-list
 * and no debt ledger — invariance is asserted in the source or not at all).
 *
 * It qualifies for the same reason `formatToParts` extraction does: the locale
 * here is a **parsing contract**, not a display choice. These categories are
 * used to look up a `defaultValue_<category>` key, and
 * `tests/unit/strings/shellKeyCoverage.test.js` asserts every plural call site
 * carries **exactly the English categories** (`_one`, `_other`) — so selecting
 * with any other language's rules would look up a key the call site provably
 * does not have, and render the raw dotted key.
 *
 * This is not the "English is fine" shortcut the rule exists to catch. Nothing
 * is FORMATTED here: no number, no date, no currency reaches a user through this
 * constant. It only picks which of two English strings the call site already
 * wrote. The real locale's plural rules are applied by i18next against the real
 * bundle, on the wired path, which never reaches this function.
 */
const INVARIANT_PARSE_LOCALE = 'en';

/**
 * Pick the `defaultValue` a call site meant for this invocation, by i18next's own
 * precedence (i18next 26.2 `Translator.translate`): with a usable `count`,
 * `defaultValue_zero` (for exactly 0), then `defaultValue_<category>`, then the
 * bare `defaultValue` — through the ONE shared selector, so this degrade path
 * picks what the wired path picks (Q-R8CLOSE-2).
 *
 * @param {{count?: number, defaultValue?: string}} options
 * @returns {string|undefined}
 */
export function selectDefaultValue(options) {
  const key = selectPluralKey(
    'defaultValue',
    options.count,
    INVARIANT_PARSE_LOCALE,
    (candidate) => typeof options[candidate] === 'string',
  );
  return options[key];
}

/**
 * Resolve a key to its call-site English, interpolated.
 *
 * Interpolating the `defaultValue` is equivalent to interpolating the English
 * leaf, because `tests/unit/strings/shellKeyCoverage.test.js` asserts every call
 * site's `defaultValue` is byte-identical to its `SHELL_STRINGS_EN` leaf. That
 * assertion is what lets this stay small instead of importing the bundle into
 * every consumer's module graph.
 *
 * @param {string} key i18next key — returned verbatim when no default is given
 * @param {object} [options] i18next-shaped options (`defaultValue`, `count`, values)
 * @returns {string}
 */
export function fallbackT(key, options = {}) {
  const template = selectDefaultValue(options);
  if (typeof template !== 'string') return key;
  return template.replace(PLACEHOLDER, (match, name) =>
    Object.prototype.hasOwnProperty.call(options, name) ? String(options[name]) : match,
  );
}

export default fallbackT;
